import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../stores/auth';
import { AuthShell, AuthSubmit, AuthNote } from '../components/AuthShell';
import { markIntroPending, releaseBootLoader } from '../lib/appReady';
import { Field, Input } from '../components/site/Form';
import { EmailCodeSteps } from '../components/site/EmailCodeSteps';

/** Where to go after signing in: `?next=` when it is a page of the app (never another site), else /app. */
function nextOf(params: URLSearchParams): string {
  const n = params.get('next') ?? '';
  return /^\/app(\/|$|\?)/.test(n) && !n.startsWith('//') ? n : '/app';
}

/**
 * The single funnel. Every stakeholder — CEO, property manager, technician, resident,
 * vendor — signs in here. What they see afterwards is decided entirely by their
 * permissions, not by which app they opened.
 */
/** Demo-account shortcuts: opt-in only (VITE_SHOW_DEMO_LOGINS=true), never in a production build. */
const SHOW_DEMO_LOGINS = import.meta.env.DEV && import.meta.env.VITE_SHOW_DEMO_LOGINS === 'true';
const DEMO_LOGINS = [
  { label: 'Chief Executive', email: 'rajiv@lotusliving.co.uk', hint: 'Rajiv Nehru · full access' },
  { label: 'Property Manager', email: 'pm@lotusliving.co.uk', hint: 'Portfolio & maintenance' },
  { label: 'Lettings', email: 'lettings@lotusliving.co.uk', hint: 'Leads, viewings, tenancies' },
  { label: 'Finance', email: 'finance@lotusliving.co.uk', hint: 'Ledger, invoices, reports' },
  { label: 'Maintenance', email: 'tech@lotusliving.co.uk', hint: 'Own assigned jobs only' },
];

export default function LoginPage() {
  const { user, login, loading, error } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = nextOf(params);
  // Forgot password (docs/plans/viewing-first-onboarding.md, phase 3): email → 6-digit code → a new password → signed in.
  const [forgot, setForgot] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // The form shows at once: nothing on this page waits for data.
  useEffect(() => { releaseBootLoader(true); }, []);

  if (user && !forgot) return <Navigate to={next} replace />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try { await login(email, password); markIntroPending(); navigate(next); }
    catch { /* The store surfaces the message. */ }
    finally { setSubmitting(false); }
  }

  if (forgot) {
    return (
      <AuthShell title={<>Forgot your <em>password?</em></>} subtitle="We'll email you a 6-digit code. Enter it, choose a new password, and you're signed in."
        below={<p>Remembered it? <button type="button" onClick={() => setForgot(false)} className="copy-link font-medium">Back to sign in</button></p>}>
        <EmailCodeSteps email={email} onSignedIn={() => { markIntroPending(); navigate(next); }} />
      </AuthShell>
    );
  }

  return (
    <AuthShell title={<>Welcome <em>back</em></>} subtitle="Sign in to your Lotus Living account."
      below={<p>Moving in? <Link to="/signup" className="copy-link font-medium">Create your resident account</Link></p>}>
      <form onSubmit={submit} className="space-y-5">
        <Field label="Email">
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="username" />
        </Field>
        <Field label="Password">
          <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
        </Field>
        <div className="-mt-2 text-right"><button type="button" onClick={() => setForgot(true)} className="copy-link text-[14px] font-medium">Forgot password?</button></div>
        {error && <AuthNote>{error}</AuthNote>}
        <div className="pt-1"><AuthSubmit disabled={submitting || loading}>{submitting ? 'Signing in…' : 'Sign in'}</AuthSubmit></div>
      </form>

      {SHOW_DEMO_LOGINS && <div className="mt-10">
        <p className="eyebrow mb-3 !text-ink-500">Demo accounts</p>
        <div className="divide-y divide-ink-200/70 overflow-hidden rounded-[12px] border border-ink-200">
          {DEMO_LOGINS.map((d) => (
            <button key={d.email} type="button" onClick={() => setEmail(d.email)} className={`flex min-h-[52px] w-full items-center justify-between gap-3 px-4 py-2.5 text-left transition-colors duration-300 hover:bg-bronze-50 ${email === d.email ? 'bg-bronze-50' : 'bg-white'}`}>
              <span className="min-w-0">
                <span className="block text-[14px] font-medium text-ink-900">{d.label}</span>
                <span className="block truncate text-[12.5px] text-ink-500">{d.hint}</span>
              </span>
              <span className={`h-2 w-2 shrink-0 rounded-full transition-colors ${email === d.email ? 'bg-bronze-500' : 'bg-ink-200'}`} />
            </button>
          ))}
        </div>
      </div>}
    </AuthShell>
  );
}
